<template>
  <div
    class="product-gallery"
    :class="`product-gallery--${responsiveLayout}`"
  >
    <product-carousel
      class="product-gallery__main-carousel"
      :active-code="activeCode"
      :current-index="currentIndex"
      :current-namespace="currentNamespace"
      :config="config.main"
      is-lightbox
      :layout="responsiveLayout"
      :loading="loading"
      :media="media"
      namespace="gallery"
      :product="product"
      :sizes="sizes"
      type="main"
      :zoom="zoom"
      @update-current-index="handleUpdateCurrentIndex"
    />
    <!-- Product gallery modal -->
    <overlay
      v-if="zoom"
      key="productGalleryModal"
      block-class="product-gallery-modal-overlay"
      direction="up"
      namespace="productGalleryModal"
      :show-overlay="false"
      :template="false"
      type="modal"
    >
      <template #body="overlay">
        <product-gallery-modal
          v-if="getComponentVisible('product-gallery-modal')"
          v-cloak
          :current-index="currentIndex"
          :current-namespace="currentNamespace"
          :media="media"
          namespace="modal"
          :overlay="overlay"
          :product="product"
          @update-current-index="handleUpdateCurrentIndex"
          @update-current-namespace="handleUpdateCurrentNamespace"
        />
      </template>
    </overlay>
  </div>
</template>

<script>

/**
 * Vue: Product gallery (product-gallery)
 * -----------------------------------------------------------------------------
 * Wrapper for product gallery carousel.
 *
 * @param {String} activeCode - Active color code.
 * @param {Number} currentIndex - Current carousel index.
 * @param {String} currentNamespace - Current carousel namespace.
 * @param {String} layout - Product gallery layout, either `horizontal`,
 * `vertical`, `tabs`, or `grid`, supports a combination in the format
 * [mobile layout]-[desktop layout].
 * @param {Boolean} [loading] - Loading state.
 * @param {Object} product - Local product object.
 * @param {String} sku - Selected swatch variant SKU.
 * @param {Boolean} [zoom] - Enable zoom functionality.
 * @param {Boolean} isGiftCard - Whether product is gift card.
 *
 * @emits updateCurrentIndex - Emit update current carousel index event.
 * @emits updateCurrentNamespace - Emit update current carousel namespace event.
 *
 */
import { defineAsyncComponent } from 'vue'
import { mapActions, mapState } from 'vuex'

import ProductCarousel from '~async/product-carousel/product-carousel'
import Overlay from '~global/overlay/overlay'

import { values as breakpoints } from '~/config/breakpoints'
import { isColorCodeInStr } from '~/helpers/product'

export default {
  name: 'ProductGallery',

  components: {
    Overlay,
    ProductCarousel,
    ProductGalleryModal: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.product-gallery-modal' */
        '~async/product-gallery-modal/product-gallery-modal'
      ),
    }),
  },

  props: {
    activeCode: {
      type: String,
      default: '',
    },
    currentIndex: {
      type: Number,
      default: 0,
    },
    currentNamespace: {
      type: String,
      default: 'gallery',
      validator: (value) => {
        return ['gallery', 'modal'].includes(value)
      },
    },
    layout: {
      type: String,
      default: 'horizontal',
      validator: (value) => {
        return value.split('-').every((part) => ['grid', 'horizontal', 'tabs', 'vertical'].includes(part))
      },
    },
    loading: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
    sku: {
      type: [String, null],
      default: null,
    },
    zoom: {
      type: Boolean,
      default: true,
    },
    isGiftCard: {
      type: Boolean,
      default: false,
    },
  },

  emits: {
    'update-current-index': (payload) => typeof payload === 'number',
    'update-current-namespace': (payload) => ['gallery', 'modal'].includes(payload),
    'update-featured-image': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      videoExists: false,
      videoHandle: null,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute carousel configs.
     * - The aim here is to convert the responsive layout into the breakpoints
     *   object supported by Splide.
     * @returns {Object}
     */
    config() {
      const mainConfigs = {
        grid: {
          arrows: false,
          destroy: true,
          pagination: false,
        },
        horizontal: {
          arrows: true,
          destroy: false,
          pagination: false,
        },
        tabs: {
          arrows: true,
          destroy: false,
          pagination: false,
        },
        vertical: {
          arrows: true,
          destroy: false,
          pagination: false,
        },
      }

      const thumbnailsConfigs = {
        shared: {
          drag: false,
          focus: 1,
          pagination: false,
          type: 'slide',
        },
        grid: {
          arrows: false,
          perMove: 1,
          perPage: 1,
        },
        horizontal: {
          arrows: true,
          gap: 'var(--spacing-m)',
          perMove: 5,
          perPage: 5,
        },
        vertical: {
          arros: true,
          direction: 'ttb',
          fixedHeight: '70px',
          gap: 'var(--spacing-xs)',
          height: 'calc((70px + var(--spacing-xs)) * 5)',
          perMove: 5,
          perPage: 5,
          trimSpace: true,
        },
        verticalBreakpoints: {
          [breakpoints.m]: {
            height: 'calc((70px + var(--spacing-xs)) * 5)',
          },
          [breakpoints.l]: {
            height: 'calc((70px + var(--spacing-xs)) * 3)',
          },
          [breakpoints.xl]: {
            height: 'calc((70px + var(--spacing-xs)) * 5)',
          },
        },
      }

      /**
       * Set configs.
       * - No difference between mobile and desktop layouts.
       */
      if (!this.layout.includes('-')) {
        const config = {
          main: mainConfigs[this.layout],
          thumbnails: {
            ...thumbnailsConfigs.shared,
            ...thumbnailsConfigs[this.layout],
          },
        }

        if (this.layout === 'vertical') {
          config.thumbnails.breakpoints = thumbnailsConfigs.verticalBreakpoints
        }

        return config
      }

      /**
       * Set mobile and desktop layouts independently.
       */
      const mobileLayout = this.layout.split('-')[0]
      const desktopLayout = this.layout.split('-')[1]

      const config = {
        main: {
          ...mainConfigs[mobileLayout],
          breakpoints: {
            [breakpoints.l]: {
              ...mainConfigs[desktopLayout],
            },
          },
        },
        thumbnails: {
          ...thumbnailsConfigs.shared,
          ...thumbnailsConfigs[mobileLayout],
          breakpoints: {
            [breakpoints.l]: {
              ...thumbnailsConfigs[desktopLayout],
            },
          },
        },
      }

      /**
       * Add vertical layout breakpoints.
       */
      if (mobileLayout === 'vertical') {
        config.thumbnails.breakpoints = thumbnailsConfigs.verticalBreakpoints[breakpoints.m]
      }

      if (desktopLayout === 'vertical') {
        config.thumbnails.breakpoints = {
          [breakpoints.l]: {
            ...config.thumbnails.breakpoints[breakpoints.l],
            ...thumbnailsConfigs.verticalBreakpoints[breakpoints.l],
          },
          [breakpoints.xl]: thumbnailsConfigs.verticalBreakpoints[breakpoints.xl],
        }
      }

      /**
       * Return config.
       */
      return config
    },

    /**
     * Compute media array.
     * @returns {Array}
     */
    media() {
      const video = this.videoExists ? this.buildVideoObject() : null

      if (!this.product?.media && this.product?.images) {
        const images = this.product.images.map((image) => {
          return this.getSourceContainsCode(image.src)
        })

        if (video) {
          video.index = images.length
          images.push(video)
        }
        return images
      }

      const productMedia = this.product.media.filter((media) => {
        return this.getSourceContainsCode(media.alt)
      })

      if (video) {
        video.index = productMedia.length
        productMedia.push(video)
      }
      return productMedia
    },

    /**
     * Compute responsive layout based on mobile.
     * @returns {String}
     */
    responsiveLayout() {
      if (!this.layout.includes('-')) {
        return this.layout
      }

      return this.isMobile
        ? this.layout.split('-')[0]
        : this.layout.split('-')[1]
    },

    /**
     * Compute whether to show thumbnails carousel.
     * @returns {Boolean}
     */
    showThumbnails() {
      return this.responsiveLayout === 'horizontal' ||
        this.responsiveLayout === 'vertical' ||
        this.responsiveLayout === 'grid'
    },

    /**
     * Compute whether to show zoom button.
     * - Button is hidden when video media is active.
     * @returns {Boolean}
     */
    showZoomButton() {
      const currentMedia = this.media[this.currentIndex]

      return (
        this.zoom &&
        !this.loading &&
        currentMedia &&
        (currentMedia.mediaType === 'image' || !currentMedia.mediaType)
      )
    },

    /**
     * Compute responsive image sizes.
     * @returns {String}
     */
    sizes() {
      return `(min-width: ${breakpoints.l}px) 50vw, 100vw`
    },
  },

  watch: {
    media() {
      if (!this.media) {
        return
      }
      this.$emit('update-featured-image', this.product.media[0].image)
    },

    /**
     * Watch active swatch color code to update video file.
     */
    activeCode() {
      this.checkVideo()
    },
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
    }),

    /**
     * Get video URL based on variant sku.
     */
    checkVideo() {
      if (!this.sku) {
        return
      }

      const videoName = `${this.sku.split('_')[0]}_${this.activeCode}`
      const videoUrl = `https://apcvideos.s3.us-east-2.amazonaws.com/${videoName}.mp4`

      this.videoHandle = videoUrl
      this.fetchVideo()
    },

    /**
     * Fetch video file from URL to check if the file exists.
     */
    fetchVideo() {
      fetch(this.videoHandle)
        .then((response) => {
          if (response.status === 200) {
            this.videoExists = true

          } else {
            this.videoExists = false
          }
        })
    },

    /**
     * Build video object to pass into media array.
     * @returns {Object}
     */
    buildVideoObject() {
      return {
        alt: this.product.title,
        mediaType: 'video',
        liquid: false,
        src: this.videoHandle,
        sources: [
          {
            format: 'mp4',
            url: this.videoHandle,
          },
        ],
      }
    },

    /**
     * Return if a specified media source includes the active code.
     * @param {String} source - Image or video source string.
     * @returns {Boolean}
     */
    getSourceContainsCode(source) {
      if (!this.activeCode && this.isGiftCard) {
        return true
      }

      return isColorCodeInStr(this.activeCode, source)
    },

    /**
     * Emit update current carousel index event.
     * @param {Number} newIndex - New media index.
     */
    handleUpdateCurrentIndex(newIndex) {
      this.$emit('update-current-index', newIndex)
    },

    /**
     * Emit update current carousel namespace event.
     * @param {Number} newNamespace - New active namespace.
     */
    handleUpdateCurrentNamespace(newNamespace) {
      this.$emit('update-current-namespace', newNamespace)
    },

    /**
     * Handle zoom click.
     */
    handleZoomClick() {
      this.openOverlay({
        component: 'product-gallery-modal',
        ignoreDismissed: true,
        namespace: 'productGalleryModal',
      })
    },
  },
}
</script>

<style lang="scss">
@import '@/components/splide';
@import './product-gallery';
@import './product-gallery-grid';
@import './product-gallery-tabs';
</style>
